/* Open House route builder — thin wrapper over the shared
 * RouteBuilderDialog. All optimization logic lives in
 * hooks/useRouteOptimizer + lib/routeOptimizer (shared with Brokerages). */
import React, { useMemo, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { shortAddress } from "@/lib/address";
import { Button } from "@/components/ui/button";
import RouteBuilderDialog from "@/components/route/RouteBuilderDialog";

function MarkAllVisitedButton({ count, onMarkAllVisited }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      onClick={async () => {
        setBusy(true);
        try {
          await (onMarkAllVisited && onMarkAllVisited());
        } finally {
          setBusy(false);
        }
      }}
      disabled={busy || count === 0}
      variant="outline"
      className="mt-2 min-h-[44px] w-full border-emerald-300 text-emerald-800 hover:bg-emerald-100"
      data-testid="route-mark-all-visited-button"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
      Mark all {count} as visited
    </Button>
  );
}

export default function RouteDialog({
  open,
  onClose,
  selectedIds,
  openHouses,
  onReorder,
  onRemoveStop,
  onMarkAllVisited,
  optimizationState,
  onApplyOptimized,
  homeCoords,
}) {
  const stops = useMemo(() => {
    const map = new Map(openHouses.map((o) => [o.id, o]));
    return selectedIds
      .map((id) => map.get(id))
      .filter(Boolean)
      .map((oh) => ({
        id: oh.id,
        lat: oh.lat,
        lng: oh.lng,
        title: shortAddress(oh.address_raw) || "—",
        subtitle: [
          oh.realtor_name || "Unassigned",
          oh.city,
          oh.neighborhood,
        ].filter(Boolean).join(" · "),
      }));
  }, [openHouses, selectedIds]);

  return (
    <RouteBuilderDialog
      open={open}
      onClose={onClose}
      stops={stops}
      onReorder={onReorder}
      onRemoveStop={onRemoveStop}
      optimizationState={optimizationState}
      onApplyOptimized={onApplyOptimized}
      homeCoords={homeCoords}
      title="Build Google Maps route"
      unitLabel="stop"
      testIdPrefix="route"
      page="open_houses"
      buildRequest={async (start_address, travel_mode) => {
        const { data } = await api.post("/openhouses/route", { ids: selectedIds, start_address, travel_mode });
        return data;
      }}
      renderResultExtras={() => (
        <MarkAllVisitedButton count={selectedIds.length} onMarkAllVisited={onMarkAllVisited} />
      )}
    />
  );
}
